import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Scorecard Component
 * Displays the Qwixx scorecard with color rows, numbers, and penalties
 *
 * @param {object} scorecard - Current scorecard state {red, yellow, green, blue, penalties}
 * @param {object} closedRows - Object indicating which rows are closed
 * @param {object} pendingRowClosures - Object indicating rows with pending closures
 * @param {number} scorecardZoom - Zoom level for scorecard (0.8-1.2)
 * @param {React.Ref} scorecardRef - Ref for scorecard container
 * @param {function} onMarkNumber - Callback when a number is clicked
 * @param {function} onZoomIn - Callback for zoom in
 * @param {function} onZoomOut - Callback for zoom out
 */
const Scorecard = ({
  scorecard,
  closedRows,
  pendingRowClosures,
  scorecardZoom,
  scorecardRef,
  onMarkNumber,
  onZoomIn,
  onZoomOut
}) => {
  const { t } = useLanguage();

  const zoomButtonStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#fff',
    border: '1px solid #ddd',
    fontSize: '20px',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 10px'
  };

  return (
    <div className="scorecard-section">
      {/* Separate heading container */}
      <div className="scorecard-heading-container">
        <h2>{t('common.yourScorecard')}</h2>
      </div>

      {/* Redesigned zoom controls with forced horizontal layout */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '10px auto',
        padding: '5px',
        width: '100%',
        maxWidth: '200px'
      }}>
        <button onClick={onZoomOut} style={zoomButtonStyle}>
          −
        </button>

        <div style={{
          fontSize: '14px',
          margin: '0 5px',
          color: '#6b7280',
          minWidth: '50px',
          textAlign: 'center'
        }}>
          {Math.round(scorecardZoom * 100)}%
        </div>

        <button onClick={onZoomIn} style={zoomButtonStyle}>
          +
        </button>
      </div>

      {/* Separate scorecard container with zoom applied */}
      <div className="scorecard-wrapper">
        <div
          ref={scorecardRef}
          className="scorecard"
          style={{
            transform: `scale(${scorecardZoom})`,
            transformOrigin: 'top center'
          }}
        >
          {/* Red Row (2-12, ascending) */}
          <div className={`row red ${closedRows.red ? 'closed' : ''} ${pendingRowClosures.red ? 'pending-closure' : ''}`}>
            <div className="row-header">{t('colors.red')}</div>
            {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
              <div
                key={`red-${num}`}
                className={`number ${scorecard.red.includes(num) ? 'marked' : ''}`}
                onClick={() => onMarkNumber('red', num)}
              >
                {num}
              </div>
            ))}
            <div
              className={`number lock ${scorecard.red.includes(12) && closedRows.red ? 'crossed' : ''} ${pendingRowClosures.red ? 'pending-marked' : ''}`}
            >
              <span role="img" aria-label="lock">{scorecard.red.length >= 5 ? '🔓' : '🔒'}</span>
            </div>
          </div>

          {/* Yellow Row (2-12, ascending) */}
          <div className={`row yellow ${closedRows.yellow ? 'closed' : ''} ${pendingRowClosures.yellow ? 'pending-closure' : ''}`}>
            <div className="row-header">{t('colors.yellow')}</div>
            {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
              <div
                key={`yellow-${num}`}
                className={`number ${scorecard.yellow.includes(num) ? 'marked' : ''}`}
                onClick={() => onMarkNumber('yellow', num)}
              >
                {num}
              </div>
            ))}
            <div
              className={`number lock ${scorecard.yellow.includes(12) && closedRows.yellow ? 'crossed' : ''} ${pendingRowClosures.yellow ? 'pending-marked' : ''}`}
            >
              <span role="img" aria-label="lock">{scorecard.yellow.length >= 5 ? '🔓' : '🔒'}</span>
            </div>
          </div>

          {/* Green Row (12-2, descending) */}
          <div className={`row green ${closedRows.green ? 'closed' : ''} ${pendingRowClosures.green ? 'pending-closure' : ''}`}>
            <div className="row-header">{t('colors.green')}</div>
            {[12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2].map(num => (
              <div
                key={`green-${num}`}
                className={`number ${scorecard.green.includes(num) ? 'marked' : ''}`}
                onClick={() => onMarkNumber('green', num)}
              >
                {num}
              </div>
            ))}
            <div
              className={`number lock ${scorecard.green.includes(2) && closedRows.green ? 'crossed' : ''} ${pendingRowClosures.green ? 'pending-marked' : ''}`}
            >
              <span role="img" aria-label="lock">{scorecard.green.length >= 5 ? '🔓' : '🔒'}</span>
            </div>
          </div>

          {/* Blue Row (12-2, descending) */}
          <div className={`row blue ${closedRows.blue ? 'closed' : ''} ${pendingRowClosures.blue ? 'pending-closure' : ''}`}>
            <div className="row-header">{t('colors.blue')}</div>
            {[12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2].map(num => (
              <div
                key={`blue-${num}`}
                className={`number ${scorecard.blue.includes(num) ? 'marked' : ''}`}
                onClick={() => onMarkNumber('blue', num)}
              >
                {num}
              </div>
            ))}
            <div
              className={`number lock ${scorecard.blue.includes(2) && closedRows.blue ? 'crossed' : ''} ${pendingRowClosures.blue ? 'pending-marked' : ''}`}
            >
              <span role="img" aria-label="lock">{scorecard.blue.length >= 5 ? '🔓' : '🔒'}</span>
            </div>
          </div>

          {/* Penalties */}
          <div className="row penalties">
            <div className="row-header">{t('penalties.label')}</div>
            {[0, 1, 2, 3].map(num => (
              <div
                key={`penalty-${num}`}
                className={`number ${scorecard.penalties > num ? 'marked' : ''}`}
              >
                X
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scorecard;
